import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useIsFocused } from '@garnish/core';
import { useOpenURL } from '@garnish/navigation';
import { useNoticeBannersStackActions } from '@garnish/overlays';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';
import { getEnvVars } from '@order/utils';

import {
  useCreateFlexOnboardingSessionMutation,
  useCustomerDataForFlexQuery,
  useFlexCustomerQuery,
} from './graphql/FlexEnrollment.generated';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Creates a flex onboarding session and opens the enrollment flow in a browser.
 */
export const useFlexEnrollment = () => {
  const isFlexEnabled = useFeatureFlag('CELS-5522-flex-enabled');
  const openURL = useOpenURL();
  const { track } = useTelemetry();
  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackActions();

  // ─── Flex Customer ─────────────────────────────────────────────────────────

  const { hasFlexPaymentMethod, isLoadingFlexCustomer } = useFlexCustomer();

  // ─── Flex Enrollment ───────────────────────────────────────────────────────

  const [customerDataResult] = useCustomerDataForFlexQuery({ pause: !isFlexEnabled });
  const isLoadingCustomerData = customerDataResult.fetching;
  const customerData = customerDataResult.data?.currentCustomer;

  const [response, createFlexOnboardingSession] = useCreateFlexOnboardingSessionMutation();
  const isLoadingFlexSession = response.fetching;

  const checkFlexEligibility = useCallback(async () => {
    track('hsafsa.landing_page.cta_tapped');

    const appUrl = getEnvVars().APP_URL;
    const returnUrl = `${appUrl}/hsa-fsa`;

    const response = await createFlexOnboardingSession({
      input: {
        firstName: customerData?.firstName ?? '',
        lastName: customerData?.lastName ?? '',
        phoneNumber: customerData?.phoneNumber ?? '',
        successUrl: returnUrl,
        cancelUrl: returnUrl,
      },
    });

    const result = response.data?.createFlexOnboardingSession;
    const redirectUrl = result?.__typename === 'FlexOnboardingSession' ? result.redirectUrl : null;

    if (redirectUrl) {
      openURL(redirectUrl);
      return;
    }

    addNoticeBanner({ palette: 'caution', text: formatMessage(messages.sessionCreationError) });
  }, [track, createFlexOnboardingSession, customerData, openURL, addNoticeBanner, formatMessage]);

  // ───────────────────────────────────────────────────────────────────────────

  return {
    isPreparingEnrollment: isLoadingFlexCustomer || isLoadingCustomerData,
    hasFlexPaymentMethod,
    isLoadingFlexSession,
    checkFlexEligibility,
  };
};

export const useFlexCustomer = (params?: UseFlexParams) => {
  const { pause = false } = params ?? {};

  const isFlexEnabled = useFeatureFlag('CELS-5522-flex-enabled');

  const [flexCustomerResult] = useFlexCustomerQuery({
    pause: !useIsFocused() || !isFlexEnabled || pause,
    requestPolicy: 'network-only',
  });

  const isLoadingFlexCustomer = flexCustomerResult.fetching;
  const flexCustomer = flexCustomerResult.data?.flexCustomerData;
  const hasFlexCustomer = flexCustomer?.__typename === 'FlexCustomerData';
  const hasFlexPaymentMethod = hasFlexCustomer && flexCustomer.flexPaymentMethods.length > 0;
  const flexPaymentMethod = hasFlexPaymentMethod ? flexCustomer.flexPaymentMethods[0] : null;

  return {
    isLoadingFlexCustomer,
    flexPaymentMethod,
    hasFlexPaymentMethod,
  };
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  sessionCreationError: {
    defaultMessage:
      'Something went wrong while checking your HSA/FSA eligibility. Please try again later.',
    description: 'Flex | Enrollment | Error',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type UseFlexParams = {
  pause?: boolean;
};
